import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { SoundManager } from '@/systems/SoundManager';
import { SaveManager } from '@/systems/SaveManager';
import { SlotManager, MAX_SLOTS, SlotSummary } from '@/systems/SlotManager';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { DesignStore } from '@/systems/DesignStore';
import { ResourceManager } from '@/systems/ResourceManager';
import { loadChallengeCompletions } from '@/map/MapData';
import { LEVEL_TREE } from '@/map/LevelTree';
import { TECH, drawTechRect, createSmallTechButton } from '@/ui/TechUI';
import { resetAllCinematicCompletions } from '@/cinematic/CinematicState';
import { resetEasterEggState } from '@/systems/EasterEggs';
import { t } from '@/i18n/I18n';

export class SaveSlotsManager {
  private slotsPanel!: Phaser.GameObjects.Container;
  private slotsOpen = false;
  private confirmPanel?: Phaser.GameObjects.Container;

  constructor(
    private scene: Phaser.Scene,
    private onStartGame: (sceneKey: string) => void,
  ) {}

  get isOpen(): boolean { return this.slotsOpen; }
  get hasConfirmPanel(): boolean { return !!this.confirmPanel; }

  /** Initialize the container (call from TitleScene.create) */
  createPanel(): void {
    this.slotsPanel = this.scene.add.container(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2)
      .setVisible(false).setDepth(20);
  }

  // ── Small tech button helper (adds to parent container) ──

  private addSmallTechButton(
    parent: Phaser.GameObjects.Container,
    x: number, y: number, label: string,
    color: number, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = createSmallTechButton(this.scene, x, y, label, color, onClick);
    parent.add(btn);
    return btn;
  }

  // ── Save Slots Panel ──

  open(): void {
    if (this.slotsOpen) { this.close(); return; }
    this.slotsOpen = true;
    this.slotsPanel.removeAll(true);

    const pw = 480;
    const ph = 480;
    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;

    // Full-screen backdrop — blocks all clicks behind, click to close
    const backdrop = this.scene.add.rectangle(-cx + CANVAS_WIDTH / 2, -cy + CANVAS_HEIGHT / 2,
      CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.3).setInteractive();
    backdrop.on('pointerdown', () => { this.close(); });
    this.slotsPanel.add(backdrop);

    // Panel frame
    const panelGfx = this.scene.add.graphics();
    panelGfx.fillStyle(0x0a0a1e, 0.95);
    panelGfx.beginPath();
    panelGfx.moveTo(-pw / 2, -ph / 2);
    panelGfx.lineTo(pw / 2, -ph / 2);
    panelGfx.lineTo(pw / 2, ph / 2 - TECH.CHAMFER);
    panelGfx.lineTo(pw / 2 - TECH.CHAMFER, ph / 2);
    panelGfx.lineTo(-pw / 2, ph / 2);
    panelGfx.closePath();
    panelGfx.fillPath();
    drawTechRect(panelGfx, -pw / 2, -ph / 2, pw, ph, TECH.COLOR, 0.5, 1.5);
    this.slotsPanel.add(panelGfx);

    // Block click-through on panel body
    const blocker = this.scene.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    this.slotsPanel.add(blocker);

    // Title
    this.slotsPanel.add(this.scene.add.text(0, -215, 'SAVE SLOTS', {
      fontSize: '16px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Close
    this.addSmallTechButton(this.slotsPanel, pw / 2 - 16, -ph / 2 + 12, '\u2715', TECH.DANGER, () => {
      SoundManager.play('clickCancel');
      this.close();
    });

    // Slot cards
    const summaries = SlotManager.getSlotSummaries();
    const cardSpacing = 130;
    const startY = -130;
    for (let i = 0; i < MAX_SLOTS; i++) {
      this.renderSlotCard(i, summaries[i], startY + i * cardSpacing);
    }

    this.slotsPanel.setVisible(true);
  }

  private renderSlotCard(index: number, summary: SlotSummary, y: number): void {
    const cardW = 430;
    const cardH = 110;
    const isActive = index === SlotManager.activeSlot;
    const left = -cardW / 2 + 10;

    // Card frame
    const cardGfx = this.scene.add.graphics();
    const borderColor = isActive ? TECH.ACCENT : TECH.DIM;
    drawTechRect(cardGfx, -cardW / 2, y - cardH / 2, cardW, cardH, borderColor, isActive ? 0.8 : 0.4);
    this.slotsPanel.add(cardGfx);

    // Row 1: Slot name + ACTIVE badge
    const title = isActive ? `Slot ${index + 1}  \u25cf ACTIVE` : `Slot ${index + 1}`;
    this.slotsPanel.add(this.scene.add.text(left, y - cardH / 2 + 6, title, {
      fontSize: '12px', color: isActive ? '#ffcc00' : '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }));

    if (summary.empty) {
      this.slotsPanel.add(this.scene.add.text(0, y + 4, '\u2014 Empty \u2014', {
        fontSize: '11px', color: '#555577', fontFamily: 'monospace',
      }).setOrigin(0.5));

      this.addSmallTechButton(this.slotsPanel, cardW / 2 - 55, y + 4, 'NEW GAME', 0x44ff88, () => {
        SoundManager.play('clickConfirm');
        this.createNewGame(index);
      });
    } else {
      const date = new Date(summary.lastPlayed);
      const dateStr = `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

      const totalMaps = LEVEL_TREE.length;
      const completed = summary.levelsCompleted;
      const progress = Math.min(1, completed / totalMaps);

      // Row 2: Map label -- shows completed count
      const mapLabel = progress >= 1 ? 'CAMPAIGN COMPLETE' : `${completed} / ${totalMaps} completed`;
      this.slotsPanel.add(this.scene.add.text(left, y - cardH / 2 + 22, mapLabel, {
        fontSize: '10px', color: progress >= 1 ? '#44ff88' : '#aaaacc', fontFamily: 'monospace',
      }));

      // Row 3: Progress bar (full width minus padding)
      const barX = left;
      const barY = y - cardH / 2 + 38;
      const barW = cardW - 24;
      const barH = 10;
      const barGfx = this.scene.add.graphics();
      barGfx.fillStyle(0x222244, 0.6);
      barGfx.fillRect(barX, barY, barW, barH);
      const fillColor = progress >= 1 ? 0x44ff88 : 0x4488ff;
      if (progress > 0) {
        barGfx.fillStyle(fillColor, 0.8);
        barGfx.fillRect(barX, barY, barW * progress, barH);
      }
      barGfx.lineStyle(1, 0x6666aa, 0.5);
      barGfx.strokeRect(barX, barY, barW, barH);
      // Percentage text on bar
      const pctText = `${Math.round(progress * 100)}%`;
      this.slotsPanel.add(barGfx);
      this.slotsPanel.add(this.scene.add.text(barX + barW / 2, barY + 1, pctText, {
        fontSize: '7px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5, 0));

      // Row 4: Stats
      this.slotsPanel.add(this.scene.add.text(left, y - cardH / 2 + 54, `$${summary.currency}  \u00b7  ${summary.componentsUnlocked} parts  \u00b7  ${summary.designCount} designs`, {
        fontSize: '9px', color: '#8888aa', fontFamily: 'monospace',
      }));

      // Row 5: Date
      this.slotsPanel.add(this.scene.add.text(left, y - cardH / 2 + 68, dateStr, {
        fontSize: '8px', color: '#555577', fontFamily: 'monospace',
      }));

      // Buttons (right side, lower)
      this.addSmallTechButton(this.slotsPanel, cardW / 2 - 95, y + 30, 'LOAD', TECH.COLOR, () => {
        SoundManager.play('clickConfirm');
        this.loadSlot(index);
      });

      this.addSmallTechButton(this.slotsPanel, cardW / 2 - 35, y + 30, '\u2715', TECH.DANGER, () => {
        SoundManager.play('click');
        this.showDeleteConfirm(index);
      });
    }
  }

  private loadSlot(index: number): void {
    const slotData = SlotManager.readSlot(index);
    if (!slotData) return;

    SlotManager.setActiveSlot(index);
    ProgressionManager.loadFromState(slotData.progression);
    DesignStore.reload();
    loadChallengeCompletions();

    this.close();
    this.onStartGame('LevelTreeScene');
  }

  /** Auto-create slot 0 and go straight to the target scene (no save panel) */
  autoCreateAndStart(sceneKey: string): void {
    SlotManager.setActiveSlot(0);
    ProgressionManager.reset();
    ResourceManager.reset();
    DesignStore.reload();
    loadChallengeCompletions();
    this.onStartGame(sceneKey);
  }

  private createNewGame(index: number): void {
    SlotManager.setActiveSlot(index);
    ProgressionManager.reset();
    ResourceManager.reset();
    DesignStore.reload();
    loadChallengeCompletions();

    // Just refresh the panel -- don't auto-start campaign
    this.close();
    this.open();
  }

  close(): void {
    this.slotsOpen = false;
    this.slotsPanel.setVisible(false);
  }

  // ── Custom delete confirmation dialog ──

  showDeleteConfirm(slotIndex: number): void {
    if (this.confirmPanel) this.confirmPanel.destroy();

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    this.confirmPanel = this.scene.add.container(cx, cy).setDepth(200);

    // Dim overlay
    const dim = this.scene.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.6)
      .setInteractive();
    this.confirmPanel.add(dim);

    // Dialog frame
    const dw = 300;
    const dh = 130;
    const dialogGfx = this.scene.add.graphics();
    dialogGfx.fillStyle(0x0a0a1e, 0.97);
    dialogGfx.beginPath();
    dialogGfx.moveTo(-dw / 2, -dh / 2);
    dialogGfx.lineTo(dw / 2, -dh / 2);
    dialogGfx.lineTo(dw / 2, dh / 2 - TECH.CHAMFER);
    dialogGfx.lineTo(dw / 2 - TECH.CHAMFER, dh / 2);
    dialogGfx.lineTo(-dw / 2, dh / 2);
    dialogGfx.closePath();
    dialogGfx.fillPath();
    drawTechRect(dialogGfx, -dw / 2, -dh / 2, dw, dh, TECH.DANGER, 0.7, 1.5);
    this.confirmPanel.add(dialogGfx);

    this.confirmPanel.add(this.scene.add.text(0, -30, `Delete Slot ${slotIndex + 1}?`, {
      fontSize: '15px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    this.confirmPanel.add(this.scene.add.text(0, -8, 'This cannot be undone.', {
      fontSize: '11px', color: '#aa6666', fontFamily: 'monospace',
    }).setOrigin(0.5));

    this.addSmallTechButton(this.confirmPanel, -60, 32, 'DELETE', TECH.DANGER, () => {
      SoundManager.play('clickConfirm');
      const wasActive = SlotManager.activeSlot === slotIndex;
      SlotManager.deleteSlot(slotIndex);
      // Reset cinematic completion flags for the deleted slot
      resetAllCinematicCompletions();
      // If deleted slot was the active one, clear in-memory state
      // so nothing accidentally re-saves it
      if (wasActive) {
        ProgressionManager.reset();    // clears in-memory data
        ResourceManager.reset();       // clears Co/Electro + localStorage
        resetEasterEggState();         // clears easter egg stats (idle timer, discord clicks, etc.)
        SaveManager.clearScoreHistory(); // score history is global -- clear it with the slot
        DesignStore.reload();          // reloads (nothing, since activeSlot=-1)
      }
      this.closeDeleteConfirm();
      this.close();
      this.open();
    });

    this.addSmallTechButton(this.confirmPanel, 60, 32, 'CANCEL', TECH.DIM, () => {
      SoundManager.play('clickCancel');
      this.closeDeleteConfirm();
    });
  }

  closeDeleteConfirm(): void {
    if (this.confirmPanel) {
      this.confirmPanel.destroy();
      this.confirmPanel = undefined;
    }
  }

  // ── Reset localStorage confirmation dialog ──

  showResetConfirm(): void {
    if (this.confirmPanel) this.confirmPanel.destroy();

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;
    this.confirmPanel = this.scene.add.container(cx, cy).setDepth(200);

    const dim = this.scene.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.6)
      .setInteractive();
    this.confirmPanel.add(dim);

    const dw = 320;
    const dh = 140;
    const dialogGfx = this.scene.add.graphics();
    dialogGfx.fillStyle(0x0a0a1e, 0.97);
    dialogGfx.beginPath();
    dialogGfx.moveTo(-dw / 2, -dh / 2);
    dialogGfx.lineTo(dw / 2, -dh / 2);
    dialogGfx.lineTo(dw / 2, dh / 2 - TECH.CHAMFER);
    dialogGfx.lineTo(dw / 2 - TECH.CHAMFER, dh / 2);
    dialogGfx.lineTo(-dw / 2, dh / 2);
    dialogGfx.closePath();
    dialogGfx.fillPath();
    drawTechRect(dialogGfx, -dw / 2, -dh / 2, dw, dh, TECH.DANGER, 0.7, 1.5);
    this.confirmPanel.add(dialogGfx);

    this.confirmPanel.add(this.scene.add.text(0, -30, t('title.resetConfirm'), {
      fontSize: '15px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    this.confirmPanel.add(this.scene.add.text(0, -8, t('title.resetWarning'), {
      fontSize: '11px', color: '#aa6666', fontFamily: 'monospace',
    }).setOrigin(0.5));

    this.addSmallTechButton(this.confirmPanel, -60, 32, t('title.reset'), TECH.DANGER, () => {
      SoundManager.play('clickConfirm');
      localStorage.clear();
      window.location.reload();
    });

    this.addSmallTechButton(this.confirmPanel, 60, 32, 'CANCEL', TECH.DIM, () => {
      SoundManager.play('clickCancel');
      this.closeDeleteConfirm();
    });
  }
}
